import {ChronoUnit, DateTimeFormatter, Instant, ZoneId} from "@js-joda/core";
import {ChronoUnitLegacy,} from "utils/time-utils";
import {DashboardTabModel, DateLoadConfig} from "api/model/dashboard/DashboardTab";
import {Dashboard, DashboardRequest, TabGroup} from "api/model/dashboard/Dashboard";
import {setDashboard} from "store/dashboard-store";
import {alignTo} from "components/dashboard/product-template-table/utils/timeUtils";
import {TreeNode} from "primereact/treenode";
import {MenuItem} from "primereact/menuitem";
import { JodaTimeUtils } from "utils/joda-time-utils";
import {I, T} from "services/lang/LanguageService";
import {APIError} from "services/HttpService";
import {easasConfirmDialog} from "../easas-global-fragment/EasasGlobalConfirmDialog";
import {easasErrorAlert} from "../easas-global-fragment/EasasGlobalAlerts";

export const normalizeChronoUnit = (unit: any) => {
  if (typeof unit === "object") {
    const { _name: name } = unit;
    return ChronoUnit[name?.toUpperCase() as keyof typeof ChronoUnit] || null;
  }
  const resolvedUnit = ChronoUnit[unit?.toString().toUpperCase() as keyof typeof ChronoUnit];
  return resolvedUnit || null;
};

const currentTimeInstantAdjusted = (
    loadConfig: DateLoadConfig,
    zoneId: ZoneId
) => {
  let referenceTime = Instant.now().atZone(zoneId)

  let dateFrom = referenceTime;
  let dateTo = referenceTime;

  if (loadConfig.alignStartTo) {
    const alignUnit = normalizeChronoUnit(loadConfig.alignStartTo);
    if (alignUnit) {
      referenceTime = JodaTimeUtils.truncateToZoned(referenceTime, alignUnit, zoneId);
    }
  }

  if (loadConfig.timeOffsetUnit) {
    const timeOffsetUnit = normalizeChronoUnit(loadConfig.timeOffsetUnit);
    if (loadConfig.timeOffset != null && timeOffsetUnit) {
      referenceTime = referenceTime.plus(loadConfig.timeOffset, timeOffsetUnit);
    }
  }

  if (loadConfig.lagUnit) {
    const lagUnit = normalizeChronoUnit(loadConfig.lagUnit);
    if (loadConfig.lag != null && lagUnit) {
      dateFrom = referenceTime.minus(loadConfig.lag, lagUnit);
    }
  }

  if (loadConfig.leadUnit) {
    const leadUnit = normalizeChronoUnit(loadConfig.leadUnit);
    if (loadConfig.lead != null && leadUnit) {
      dateTo = referenceTime.plus(loadConfig.lead, leadUnit);
    }
  }

  const getDateTo = () => {
    if (loadConfig.leadUnit === ChronoUnitLegacy.YEARS) {
      return  dateTo.minus(1, ChronoUnit.NANOS);
    }

    return dateTo
  };

  const lagLeadDefined = loadConfig.lag != null && loadConfig.lead != null;

  return {
    referenceTime: loadConfig.alignStartTo != null ? referenceTime.toInstant(): undefined,
    dateFrom: lagLeadDefined ? dateFrom.toInstant() : undefined,
    dateTo: lagLeadDefined ? getDateTo().toInstant() : undefined,
  };
}


const adjustReferenceTime = (
  loadConfig: DateLoadConfig,
  zoneId: ZoneId,
) => {
  let referenceTime = Instant.now().atZone(zoneId).toLocalDateTime();
  
  let dateFrom = referenceTime;
  let dateTo = referenceTime;

  if (loadConfig.alignStartTo) {
    const alignUnit = normalizeChronoUnit(loadConfig.alignStartTo);
    if (alignUnit) {
      referenceTime = alignTo(referenceTime, alignUnit);
    }
  }

  if (loadConfig.timeOffsetUnit) {
    const timeOffsetUnit = normalizeChronoUnit(loadConfig.timeOffsetUnit);
    if (loadConfig.timeOffset != null && timeOffsetUnit) {
      referenceTime = referenceTime.plus(loadConfig.timeOffset, timeOffsetUnit);
    }
  }
  if (loadConfig.lagUnit) {
    const lagUnit = normalizeChronoUnit(loadConfig.lagUnit);
    if (loadConfig.lag != null && lagUnit) {
      dateFrom = referenceTime.minus(loadConfig.lag, lagUnit);
    }
  }

  if (loadConfig.leadUnit) {
    const leadUnit = normalizeChronoUnit(loadConfig.leadUnit);
    if (loadConfig.lead != null && leadUnit) {
      dateTo = referenceTime.plus(loadConfig.lead, leadUnit);
    }
  }

  const getDateTo = () => {
    if (loadConfig.leadUnit === ChronoUnitLegacy.YEARS) {
      const dateToMinusOneMinute = dateTo.minus(1, ChronoUnit.NANOS);
      return dateToMinusOneMinute.format(DateTimeFormatter.ISO_LOCAL_DATE_TIME).toString();
    }
    return dateTo.format(DateTimeFormatter.ISO_LOCAL_DATE_TIME).toString();
  };

  return {
    referenceTime:
      loadConfig.alignStartTo != null
        ? referenceTime.format(DateTimeFormatter.ISO_LOCAL_DATE_TIME).toString()
        : undefined,
    dateFrom:
      loadConfig.lag != null && loadConfig.lead != null
        ? dateFrom.format(DateTimeFormatter.ISO_LOCAL_DATE_TIME)
        : undefined,
    dateTo: loadConfig.lag != null && loadConfig.lead != null ? getDateTo() : undefined,
  };
};

const onDragEndSplice = <T>(result: any, itemsToReorder: T[]): T[] => {
  const reorderedItems = Array.from(itemsToReorder);
  const [removed] = reorderedItems.splice(result.source.index, 1);
  reorderedItems.splice(result.destination.index, 0, removed);

  return reorderedItems;
};

const updateDashboardTabs = (dashboard: Dashboard, newTabs: Array<DashboardTabModel>) => {
  const updatedDashboard = {
    ...dashboard,
    dashboardTabsJs: newTabs,
  } as Dashboard;
  setDashboard(updatedDashboard);

  return updatedDashboard;
};

const tabUpdateInDashboard = (
  dashboardTabs: any[],
  currentTabIndex: number,
  updatedTabContent: any
) => {
  return [
    ...dashboardTabs.slice(0, currentTabIndex),
    {
      ...dashboardTabs[currentTabIndex],
      ...updatedTabContent,
    },
    ...dashboardTabs.slice(currentTabIndex + 1),
  ];
};

const getFlatNodeMap = (treeData: TreeNode[]): Record<string, TreeNode> => {
  const map: Record<string, TreeNode> = {};

  const recurse = (nodes: TreeNode[]) => {
    for (let i = 0; i < nodes.length; i++) {
      const node = nodes[i];
      map[node.key as string] = node;
      if (node.children) recurse(node.children);
    }
  };

  recurse(treeData);
  return map;
};

const getMenuItems = (
  treeNodes: TreeNode[],
  onSelect: (tabUUID: string) => void
): MenuItem[] => {
  return treeNodes.map((node): MenuItem => {
    if (node.children) {
      return {
        label: node.label,
        items: getMenuItems(node.children, onSelect),
      };
    }
    return {
      label: node.label,
      command: () => onSelect(node.key as string),
    };
  });
};

const getTreeSelectOptions = (tabs: DashboardTabModel[], tabGroups: TabGroup[]): TreeNode[] => {
  if (tabGroups.length === 0) {
    return tabs.map(tab => ({
      key: tab.uuid,
      label: I(tab.title),
      data: tab,
    }));
  }

  const groupMap = new Map(tabGroups.map(group => [group.uuid, group]));

  const grouped: TreeNode[] = tabGroups
    .map(group => {
      const groupTabs = tabs.filter(tab => tab.tabGroupId === group.uuid);
      const isTabPlaceholder = group.isTabPlaceholder && groupTabs.length === 1;

      if (isTabPlaceholder) {
        const tab = groupTabs[0];
        return {
          key: tab.uuid,
          label: I(tab.title),
          data: tab,
        };
      }

      if (groupTabs.length === 0) {
        return { key: "", label: "", children: [] };
      }
      return {
        key: group.uuid,
        label: group.title,
        children: groupTabs.map(tab => ({
          key: tab.uuid,
          label: I(tab.title),
          data: tab,
        })),
      };
    })
    .filter(node => node.key !== "");

  const ungrouped: TreeNode[] = tabs
    .filter(tab => !tab.tabGroupId || !groupMap.has(tab.tabGroupId))
    .map(tab => ({
      key: tab.uuid,
      label: I(tab.title),
      data: tab,
    }));

  return [...ungrouped, ...grouped];
};

const getFlatTabList = (treeNodes: TreeNode[]): TreeNode[] => {
  const flatList: TreeNode[] = [];

  const flatten = (nodes: TreeNode[]) => {
    nodes.forEach(node => {
      if (node.children && node.children.length > 0) {
        flatten(node.children);
      } else {
        flatList.push(node);
      }
    });
  };

  flatten(treeNodes);
  return flatList;
};

const dashboardUpdateErrorHandler = (
  error: APIError,
  updateCb: (variables: DashboardRequest) => void,
  dashboard: Dashboard
) => {
  if (error.status === 409) {
    easasConfirmDialog({
      header: T("confirm-popup.dashboard-overwrite-header"),
      message: error.message,
      acceptText: T("action.overwrite"),
      cancelText: T("action.cancel"),
      confirmedCallback: () => {
        updateCb({ ...dashboard, overwrite: true });
      },
    });
  } else {
    easasErrorAlert(error.message ?? "-");
  }
};

export {
  onDragEndSplice,
  updateDashboardTabs,
  tabUpdateInDashboard,
  adjustReferenceTime,
  currentTimeInstantAdjusted,
  getFlatNodeMap,
  getMenuItems,
  getTreeSelectOptions,
  dashboardUpdateErrorHandler,
  getFlatTabList,
};
